import { reactive } from "vue";
import { type Action } from "@src/api";

type State = {
  ids: string[];
  map: Record<string, Action>;
  byItemId: Record<string, string[]>;
};

const state = reactive<State>({
  ids: [],
  map: {},
  byItemId: {},
});

export const useStateActionList = () => {
  const setActionList = (actions: Action[]) => {
    const ids = [];
    const map = {};
    const byItemId = {};

    actions.forEach((action) => {
      ids.push(action.action_id);
      map[action.action_id] = action;

      if (!byItemId[action.item_id]) byItemId[action.item_id] = [];

      byItemId[action.item_id].push(action.action_id);
    });

    state.ids = ids;
    state.map = map;
    state.byItemId = byItemId;
  };

  const clearActionList = () => {
    state.ids = [];
    state.map = {};
  };

  const updateAction = (action: Pick<Action, "action_id"> & Partial<Action>) => {
    state.map[action.action_id] = { ...state.map[action.action_id], ...action };
  };

  const getActionByItemId = (itemId: string, actionType?: string) => {
    if (!state.byItemId[itemId]) return null;

    const actionId = state.byItemId[itemId].find(
      (actionId) =>
        (actionType ? state.map[actionId].action_type === actionType : true) &&
        state.map[actionId].status !== "done" &&
        state.map[actionId].status !== "canceled",
    );

    return actionId ? state.map[actionId] : null;
  };

  const getCountActions = (params?: { itemId?: string; status?: "request" | "done" | "cancelled" }) => {
    if (!params) return state.ids.length;
    if (params.status && !params.itemId)
      return state.ids.filter((actionId) => state.map[actionId].status === params.status).length;
    if (params.itemId && !state.byItemId[params.itemId]) return 0;
    if (params.itemId && !params.status) return state.byItemId[params.itemId].length;

    return state.byItemId[params.itemId].filter((actionId) => state.map[actionId].status === params.status).length;
  };

  const getActionsGroupedByStatus = (itemId: string) => {
    const mapByStatus = (state.byItemId[itemId] || []).reduce<
      Record<string, Pick<Action, "status" | "reason"> & { count: number }>
    >((acc, actionId) => {
      const action = state.map[actionId];

      if (!acc[action.status]) {
        acc[action.status] = {
          status: action.status,
          reason: action.reason,
          count: 0,
        };
      }

      acc[action.status].count++;

      return acc;
    }, {});

    return Object.values(mapByStatus);
  };

  return {
    value: state,
    set: setActionList,
    update: updateAction,
    clear: clearActionList,
    getByItemId: getActionByItemId,
    getCountActions: getCountActions,
    getActionsGroupedByStatus: getActionsGroupedByStatus,
  };
};
